import React from "react";
import { Activity, Equal, Percent, Scale, TrendingUp } from "lucide-react";

function Bar({ label, pct, color }) {
    const w = Math.min(100, Math.max(0, pct || 0));
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-[10px]">
                <span className="text-slate-500">{label}</span>
                <span className="text-slate-400">{w.toFixed(1)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${color}`}
                    style={{ width: `${w}%` }}
                />
            </div>
        </div>
    );
}

const YONTEM_STYLES = {
    resmi: "border-amber-500/30 bg-amber-500/5",
    mbs_hibrit: "border-emerald-500/30 bg-emerald-500/5",
    serbest_piyasa: "border-sky-500/30 bg-sky-500/5",
};

export default function HesapNetPanel({ hesap }) {
    if (!hesap) return null;

    const adimlar = hesap.formul_adimlari || [];
    const zincir = hesap.hesap_zinciri || [];
    const oranlar = hesap.net_oranlar || {};
    const parcalar = hesap.parcalar || [];
    const kartlar = hesap.yontem_kartlari || [];
    const secili = hesap.secili_yontem || hesap.onerilen_yontem;

    return (
        <div className="space-y-5">
            {kartlar.length > 1 ? (
                <div className="grid md:grid-cols-3 gap-3">
                    {kartlar.map((k) => {
                        const active = k.yontem === secili;
                        return (
                            <div
                                key={k.yontem}
                                className={`rounded-xl border p-4 text-center transition-all ${
                                    YONTEM_STYLES[k.yontem] || "border-white/10 bg-black/20"
                                } ${active ? "ring-2 ring-[#D4AF37]/50" : ""}`}
                            >
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-center gap-1">
                                    <Scale className="h-3 w-3" />
                                    {k.etiket || k.yontem}
                                </div>
                                <div className="text-xl font-display text-white">{k.gercek_deger_kaybi_fmt || "—"}</div>
                                {active ? (
                                    <div className="text-[10px] text-[#D4AF37] mt-1">Seçili / önerilen</div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}

            {hesap.onerilen_gerekce ? (
                <p className="text-xs text-slate-400 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">
                    {hesap.onerilen_gerekce}
                </p>
            ) : null}

            {(hesap.uyari_notlari || []).length ? (
                <div className="space-y-1">
                    {(hesap.uyari_notlari || []).slice(0, 4).map((n, i) => (
                        <p key={i} className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-1.5">
                            {n}
                        </p>
                    ))}
                </div>
            ) : null}

            <div className="relative overflow-hidden rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-950/80 via-[#0a1628] to-black p-6 text-center">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(52,211,153,0.15),transparent_60%)]" />
                <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/80 mb-1">
                        Net gerçek değer kaybı {secili ? `· ${secili}` : ""}
                    </div>
                    <div className="font-display text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-emerald-400 to-teal-300">
                        {hesap.gercek_deger_kaybi_fmt}
                    </div>
                    {hesap.gercek_ham_fmt && hesap.gercek_ham !== hesap.gercek_deger_kaybi ? (
                        <p className="text-xs text-slate-500 mt-1 line-through">{hesap.gercek_ham_fmt}</p>
                    ) : null}
                    {hesap.rayic_hasarli_fmt ? (
                        <p className="text-xs text-sky-300/80 mt-2">Onarım sonrası rayiç: {hesap.rayic_hasarli_fmt}</p>
                    ) : null}
                    {hesap.sinir_notu ? (
                        <p className="text-xs text-amber-300/90 mt-2 px-3 py-1.5 rounded-lg bg-amber-500/10 inline-block">
                            {hesap.sinir_notu}
                        </p>
                    ) : null}
                </div>
            </div>

            {zincir.length ? (
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                    <h4 className="text-xs font-medium text-white flex items-center gap-2 mb-3">
                        <Equal className="h-3.5 w-3.5 text-[#D4AF37]" />
                        Hesap zinciri (net)
                    </h4>
                    <div className="space-y-2">
                        {zincir.map((z, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-2 text-xs border-l-2 border-emerald-500/40 pl-3 py-1"
                            >
                                <span className="text-slate-500 shrink-0 w-5">{i + 1}.</span>
                                <span className="text-slate-300 flex-1">{z.aciklama}</span>
                                <span className="text-emerald-300 font-mono font-medium">{z.sonuc_fmt}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="grid sm:grid-cols-2 gap-3">
                {oranlar.dk_rayic_yuzde != null ? (
                    <div className="rounded-lg border border-white/10 p-3 bg-white/[0.02]">
                        <Percent className="h-4 w-4 text-violet-400 mb-1" />
                        <div className="text-[10px] text-slate-500">DK / Rayiç</div>
                        <div className="text-lg text-white">%{oranlar.dk_rayic_yuzde}</div>
                    </div>
                ) : null}
                {oranlar.piyasa_dusus_yuzde != null ? (
                    <div className="rounded-lg border border-white/10 p-3 bg-white/[0.02]">
                        <TrendingUp className="h-4 w-4 text-sky-400 mb-1" />
                        <div className="text-[10px] text-slate-500">Piyasa düşüş</div>
                        <div className="text-lg text-white">%{oranlar.piyasa_dusus_yuzde}</div>
                    </div>
                ) : null}
                {oranlar.resmi_hasar_sinifi ? (
                    <div className="rounded-lg border border-white/10 p-3 bg-white/[0.02]">
                        <Activity className="h-4 w-4 text-amber-400 mb-1" />
                        <div className="text-[10px] text-slate-500">Resmi hasar sınıfı</div>
                        <div className="text-lg text-white">{oranlar.resmi_hasar_sinifi}</div>
                    </div>
                ) : null}
                {oranlar.dk_fatura_yuzde != null ? (
                    <div className="rounded-lg border border-white/10 p-3 bg-white/[0.02]">
                        <Percent className="h-4 w-4 text-rose-400 mb-1" />
                        <div className="text-[10px] text-slate-500">DK / Fatura</div>
                        <div className="text-lg text-white">%{oranlar.dk_fatura_yuzde}</div>
                    </div>
                ) : null}
            </div>

            {adimlar.length ? (
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                    <h4 className="text-xs font-medium text-white mb-3">Formül adımları</h4>
                    <div className="space-y-2">
                        {adimlar.map((a, i) => (
                            <div key={i} className="flex justify-between gap-2 text-xs border-b border-white/5 pb-2">
                                <div>
                                    <div className="text-slate-300">{a.adim}</div>
                                    {a.formul ? <div className="text-slate-500 font-mono text-[10px]">{a.formul}</div> : null}
                                    {a.aciklama ? <div className="text-slate-500 text-[10px]">{a.aciklama}</div> : null}
                                </div>
                                <div className="text-emerald-300 font-mono shrink-0">{a.deger_fmt ?? a.deger}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {parcalar.length ? (
                <div className="rounded-xl border border-white/10 overflow-hidden">
                    <div className="px-4 py-2 bg-white/[0.03] text-[10px] uppercase tracking-wider text-slate-500">
                        Parça katkıları
                    </div>
                    <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
                        {parcalar.map((p, i) => (
                            <div key={i} className="px-4 py-2 flex justify-between text-xs">
                                <span className="text-slate-300">{p.etiket || p.parca_adi}</span>
                                <span className="text-emerald-300 font-mono">{p.katki_tl_fmt || p.katsayi}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {(hesap.km_band_resmi || hesap.km_band_mbs) && (
                <div className="grid sm:grid-cols-2 gap-2 text-[10px] text-slate-500">
                    {hesap.km_band_resmi ? <p>Resmi KM: {hesap.km_band_resmi}</p> : null}
                    {hesap.km_band_mbs ? <p>MBS KM: {hesap.km_band_mbs}</p> : null}
                </div>
            )}

            {(hesap.hasar_katsayisi_parcadan != null || hesap.km_katsayisi != null) && (
                <div className="grid sm:grid-cols-3 gap-2 text-[10px] text-slate-500">
                    {hesap.hasar_katsayisi_parcadan != null ? (
                        <p>Parça hasar k.: {(Number(hesap.hasar_katsayisi_parcadan) * 100).toFixed(0)}%</p>
                    ) : null}
                    {hesap.hasar_katsayisi != null ? (
                        <p>Uygulanan hasar k.: {(Number(hesap.hasar_katsayisi) * 100).toFixed(0)}%</p>
                    ) : null}
                    {hesap.km_katsayisi != null ? (
                        <p>KM kats.: {Number(hesap.km_katsayisi).toFixed(2)}</p>
                    ) : null}
                </div>
            )}

            {oranlar.hasar_katsayi_yuzde != null ? (
                <Bar label="Hasar katsayısı (tavan %90)" pct={oranlar.hasar_katsayi_yuzde} color="bg-amber-500" />
            ) : null}
        </div>
    );
}
